import '../../UsersScreen/UserTable.css'
import { useEffect, useState } from "react"
import {
    useReactTable,
    getCoreRowModel,
    flexRender,
} from "@tanstack/react-table"
import { useDispatch, useSelector } from "react-redux"
import { toast } from "react-toastify"
import { Link } from "react-router-dom"

import { fetchEditExam, fetchExams, searchExams } from "../ExamApi"
import { setLoading } from "../../../services/global/globalSlice"
import { selectTranslations } from "../../../services/i18n/i18nSlice"
import { EXAMS_STATUS } from "../../../config/constants"
import ConfirmModal from "../../../components/_modals/ConfirmModal"

import open_icon from '../../../assets/images/open.svg'
import trash_icon from '../../../assets/images/trash.svg'
import copy_icon from '../../../assets/images/copy.svg'
import chart_icon from '../../../assets/images/exams/chart.svg'

export default function ExamsTable (props) {

    const t = useSelector(selectTranslations)
    const dispatch = useDispatch()

    const [examsData, setExamsData] = useState([])
    const [cursorHistory, setCursorHistory] = useState([null])
    const [pageIndex, setPageIndex] = useState(0)
    const [nextCursor, setNextCursor] = useState(null)
    const [confirmModal, setConfirmModal] = useState({
        title: "",
        text: "",
        onClose: null,
        onConfirm: null,
        confirmCta: "",
        closeCta: ""
    })

    const loadExams = async (cursor) => {
        dispatch(setLoading(true))
        const _params = {
            ...(props?.filter?.limit && {limit: props?.filter?.limit} ),
            ...(props?.filter?.state && {state: props?.filter?.state} ),
            ...(props?.filter?.started !== null && {started: props?.filter?.started} ),
            ...(props?.filter?.ended !== null && {ended: props?.filter?.ended} ),
            ...(props?.filter?.search_pattern && {search_pattern: props?.filter?.search_pattern} ),
            ...(cursor && {start_cursor: cursor})
        }
        const response = props?.filter?.search_pattern ? await searchExams(_params) : await fetchExams(_params)
        if(response.success){
            const _exams = props?.filter?.state === 'deleted'
                ? response?.data?.exams || []
                : (response?.data?.exams || []).filter(e => e.state !== 'deleted')
            setExamsData(_exams)
            setNextCursor(response?.data?.next_cursor)
        } else {
            toast.error(t.exams.errors.loading)
        }
        dispatch(setLoading(false))
    }

    const closeConfirmModal = () => {
        setConfirmModal({
            ...confirmModal,
            show:false
        })
    }

    const prepareExamData = (exam) => {
        const _data = structuredClone(exam)
        if(_data['start_date'])
            _data['start_date'] = `${new Date(_data['start_date']).toISOString().substring(0,19)}+00:00`
        if(_data['end_date'])
            _data['end_date'] = `${new Date(_data['end_date']).toISOString().substring(0,19)}+00:00`
        else if(_data['end_date'] === "")
            delete _data.end_date
        if(_data['time'])
            _data['time'] = parseInt(_data['time'])
        if(_data['thresholds']?.length > 0)
            _data['thresholds'] = _data['thresholds']?.map((t)=>{
                if(t.threshold_name)
                    return {
                        threshold_name: t.threshold_name,
                        score: parseInt(t.score),
                        threshold_type: t.threshold_type
                    }
            })
        return _data
    }

    const deleteExam = async (exam, force) => {
        if(!force){
            setConfirmModal({
                ...confirmModal,
                show:true,
                title: t.exam.delete_title,
                text: t.exam.delete_text,
                onClose: ()=>closeConfirmModal(),
                onConfirm: ()=>deleteExam(exam, true),
                confirmCta: t.exam.delete_cta,
                closeCta: t.modals.abort,
            })
        } else {
            dispatch(setLoading(true))
            const _examData = prepareExamData(exam)
            _examData['state'] = 'deleted'
            const response = await fetchEditExam(_examData?.exam_id, _examData)
            if(response.success){
                toast.success(t.exam.deleted_exam)
                loadExams(cursorHistory[pageIndex])
            } else {
                toast.error(t.exam.error_saving_exam)
            }
            dispatch(setLoading(false))
            closeConfirmModal()
        }
    }

    const handleNext = () => {
        if(nextCursor){
            const newHistory = [...cursorHistory.slice(0, pageIndex + 1), nextCursor]
            setCursorHistory(newHistory)
            setPageIndex(pageIndex + 1)
            loadExams(nextCursor)
        }
    }

    const handlePrevious = () => {
        if(pageIndex > 0){
            const newIndex = pageIndex - 1
            setPageIndex(newIndex)
            loadExams(cursorHistory[newIndex])
        }
    }

    const getExamStatus = (exam) => {
        if(exam?.state === "ready" && exam?.end_date && new Date(exam.end_date) <= new Date())
            return {label: t.exams.past, ...EXAMS_STATUS['past']}
        return {label: t.exams[exam?.state], ...EXAMS_STATUS[exam?.state]}
    }

    const columns = [
        {
            accessorFn: (row) => row.title,
            header: t.exam.title,
            cell: ({row}) => (
                <div className='d-flex align-items-center'>
                    <span>{row.original.title}</span>
                    {
                        row.original.is_customizable ?
                            <span className='status_label ms-2' style={{color:"#0A5ADB", background:"#E4EFFF"}}>{t.exams.customizable_badge}</span>
                        : null
                    }
                </div>
            )
        },
        {
            id: "state",
            header: t.exams.state,
            cell: ({row}) => {
                const status = getExamStatus(row.original)
                return <span className='status_label' style={{color: status.color, background: status.background}}>{status.label}</span>
            }
        },
        {
            accessorFn: (row) => row.modules_count,
            header: t.exam.modules,
        },
        {
            accessorFn: (row) => row.questions_count,
            header: t.exam.questions,
        },
        {
            accessorFn: (row) => row.start_date ? new Date(row.start_date).toLocaleString() : "-",
            header: t.exam.start_date,
        },
        {
            id: "actions",
            header: "",
            cell: ({row}) => (
                <div className='d-flex justify-content-end'>
                    <Link className="ms-3" to={`/exams/user_exams/${row.original.exam_id}`}>
                        <img src={chart_icon} />
                        {t.exams.open_user_exams}
                    </Link>
                    <Link className="ms-3" to={`/exams/details/${row.original.exam_id}`}>
                        <img src={open_icon} />
                        {t.exams.open_edit}
                    </Link>
                    <Link className="ms-3" to={'/exams/create'} state={{duplicateFrom: row.original.exam_id}}>
                        <img src={copy_icon} />
                        {t.actions.duplicate}
                    </Link>
                    <a className="ms-3" href="#" style={{opacity: row.original.state === "deleted" ? 0.5 : 1, pointerEvents: row.original.state === "deleted" ? "none" : "auto"}} onClick={(e)=>{e.preventDefault(); deleteExam(row.original)}}>
                        <img src={trash_icon} />
                        <span className="text-danger">{t.exams.delete_exam}</span>
                    </a>
                </div>
            )
        },
    ]

    const table = useReactTable({
        data: examsData,
        columns,
        getCoreRowModel: getCoreRowModel(),
    })

    useEffect(()=>{
        setCursorHistory([null])
        setPageIndex(0)
        loadExams(null)
    },[props.filter])

    return (
        <div className='userTable'>
            <table>
                <thead>
                    {table.getHeaderGroups().map((headerGroup) => (
                        <tr key={headerGroup.id}>
                            {headerGroup.headers.map((header) => (
                                <th key={header.id}>
                                    {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                                </th>
                            ))}
                        </tr>
                    ))}
                </thead>
                <tbody>
                    {table.getRowModel().rows.map((row) => (
                        <tr key={row.id}>
                            {row.getVisibleCells().map((cell) => (
                                <td key={cell.id}>
                                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
            {
                !examsData || examsData.length === 0 ?
                    <div className="text-center py-3">
                        <h2>{t.exams.no_exam_available}</h2>
                    </div>
                : null
            }
            <div className='pagination d-flex align-items-center justify-content-between mt-3'>
                <span className={pageIndex === 0 ? "disabled" : ""} style={{cursor: pageIndex === 0 ? "default" : "pointer"}} onClick={handlePrevious}>
                    {t.exams.previous}
                </span>
                <span>{t.exams.page} {pageIndex + 1}</span>
                <span className={!nextCursor ? "disabled" : ""} style={{cursor: !nextCursor ? "default" : "pointer"}} onClick={handleNext}>
                    {t.exams.next}
                </span>
            </div>
            <ConfirmModal props={confirmModal}/>
        </div>
    )
}
